import { LANGUAGE_PROFICIENCIES_KEYS, PositionsConst } from '@axiom/const';
import type { MatchChecklistItemType } from '@axiom/ui';
import { MatchChecklistIcons, TaxonomyUtil } from '@axiom/ui';
import type {
  AccountIntake,
  AfcSubmission,
  AfcSubmissionCandidate,
  Candidate,
  CandidateLegalTechSkill,
  Experience,
  PositionWorksitesType,
  Taxonomy,
} from '@axiom/validation';

export type ChecklistSection = {
  name: string;
  header: string;
  items: MatchChecklistItemType[];
};

export type ChecklistCategory = {
  name: string;
  sections: ChecklistSection[];
};

const { Onsite, OnsiteRemote } = PositionsConst.Worksites;

const getValidStates = (desiredState: string): string[] => {
  if (desiredState === 'new york') {
    return [desiredState, 'connecticut', 'new jersey'];
  }

  if (desiredState === 'district of columbia') {
    return [desiredState, 'maryland', 'virginia'];
  }

  return [desiredState];
};

interface GenerateChecklistProps {
  candidate: AfcSubmissionCandidate;
  intakeData: AccountIntake;
  legalTechSkills: CandidateLegalTechSkill[];
  submission: AfcSubmission;
  taxonomy: Taxonomy;
}

export const EngagementTalentMatchChecklistUtil = {
  generateChecklist({
    candidate,
    intakeData,
    legalTechSkills,
    submission,
    taxonomy,
  }: GenerateChecklistProps): ChecklistCategory[] {
    const allFocusAreas = [
      ...new Set([
        intakeData.primaryFocusArea,
        ...(intakeData.otherFocusAreas?.sort((a, b) => {
          const labelA = taxonomy.focusAreas[a]?.fullName || '';
          const labelB = taxonomy.focusAreas[b]?.fullName || '';
          return labelA.toLowerCase().localeCompare(labelB.toLowerCase());
        }) || []),
      ]),
    ];

    const focusAreaChecklistItems: MatchChecklistItemType[] = allFocusAreas
      .map(focusArea =>
        this.getFocusAreaChecklistItem(
          candidate.experiences,
          focusArea,
          taxonomy
        )
      )
      .filter(item => !!item);

    const talentProfileFocusAreaChecklistItems = focusAreaChecklistItems.filter(
      focusAreaChecklistItem =>
        focusAreaChecklistItem.name.startsWith('checklist-item-focus-area-')
    );

    const aiDetectedFocusAreaChecklistItems = focusAreaChecklistItems.filter(
      focusAreaChecklistItem =>
        focusAreaChecklistItem.name.startsWith('checklist-item-ai-focus-area-')
    );

    const sortedLegalSkills =
      intakeData.legalSkills?.sort((skillA, skillB) => {
        const labelA = taxonomy.legalSkills[skillA]?.fullName || '';
        const labelB = taxonomy.legalSkills[skillB]?.fullName || '';
        return labelA.toLowerCase().localeCompare(labelB.toLowerCase());
      }) || [];

    const legalSkillsChecklistItems: MatchChecklistItemType[] =
      sortedLegalSkills
        .map(legalSkill =>
          this.getLegalSkillsChecklistItem(
            candidate.experiences,
            legalSkill,
            taxonomy
          )
        )
        .filter(item => !!item);

    const talentReportedLegalSkillsChecklistItems =
      legalSkillsChecklistItems.filter(focusAreaChecklistItem =>
        focusAreaChecklistItem.name.startsWith('checklist-item-legal-skill')
      );

    const aiDetectedLegalSkillsChecklistItems =
      legalSkillsChecklistItems.filter(focusAreaChecklistItem =>
        focusAreaChecklistItem.name.startsWith('checklist-item-ai-legal-skill')
      );

    const talentProfileChecklist: MatchChecklistItemType[] = [
      this.getAvailabilityChecklistItem(
        candidate.weeklyAvailability,
        intakeData.billingUnitsPerWeek
      ),
      this.getLocationChecklistItem(
        submission.addressState,
        submission.positions[0]?.worksite,
        candidate.addressState
      ),
      this.getIndustryChecklistItem(candidate.industries, intakeData, taxonomy),
      ...talentProfileFocusAreaChecklistItems,
      this.getLanguageChecklistItems(
        intakeData.languageId,
        candidate.languages
      ),
    ].filter(item => !!item);

    const aiDetectedExperience: MatchChecklistItemType[] =
      aiDetectedFocusAreaChecklistItems;

    const talentReportedSkills: MatchChecklistItemType[] = [
      ...talentReportedLegalSkillsChecklistItems,
      ...this.getLegalTechSkillsChecklistItems(
        intakeData.legalTechSkills,
        legalTechSkills,
        taxonomy
      ),
      ...this.getGeneralSkillsChecklistItems(
        intakeData.generalSkills,
        candidate.experiences,
        taxonomy
      ),
    ];

    const aiDetectedSkills: MatchChecklistItemType[] = [
      ...aiDetectedLegalSkillsChecklistItems,
    ];

    return [
      {
        name: 'CANDIDATE_PROFILE_CHECKLIST',
        sections: [
          {
            name: 'TALENT_PROFILE',
            header: 'Talent profile checklist',
            items: talentProfileChecklist,
          },
          {
            name: 'AI_DETECTED_EXPERIENCE',
            header: 'AI-detected experience from talent profile',
            items: aiDetectedExperience,
          },
        ],
      },
      {
        name: 'CANDIDATE_SKILLS_CHECKLIST',
        sections: [
          {
            name: 'TALENT_REPORTED_SKILLS',
            header: 'Talent-reported skills',
            items: talentReportedSkills,
          },
          {
            name: 'AI_DETECTED_SKILLS',
            header: 'AI-detected skills from talent experience',
            items: aiDetectedSkills,
          },
        ],
      },
    ];
  },

  hasChecklistItems(categories: ChecklistCategory[]) {
    return categories.some(category =>
      category.sections.some(section => section.items.length > 0)
    );
  },

  getIndustryChecklistItem(
    industries: Candidate['industries'],
    intakeData: AccountIntake,
    taxonomy: Taxonomy
  ): MatchChecklistItemType | undefined {
    if (!industries?.length || !intakeData.industry) {
      return undefined;
    }

    const matchedIndustryValue = industries.find(
      industry => industry.industryValue === intakeData.industry
    )?.industryValue;

    if (!matchedIndustryValue) {
      return undefined;
    }

    const industryLabel = TaxonomyUtil.getFilteredIntakeFormIndustryOptions(
      taxonomy.industries
    ).find(industryItem => industryItem.value === matchedIndustryValue)?.label;

    if (!industryLabel) {
      return undefined;
    }

    return {
      name: 'checklist-item-industry',
      icon: MatchChecklistIcons.Matched,
      children: `${industryLabel} industry experience`,
    };
  },

  getLanguageChecklistItems(
    requiredLanguageId: AccountIntake['languageId'],
    candidateLanguages: Candidate['languages']
  ): MatchChecklistItemType | undefined {
    const matchedLanguages =
      candidateLanguages?.filter(
        c =>
          c.languageId === requiredLanguageId &&
          (c.languageProficiency === LANGUAGE_PROFICIENCIES_KEYS.PROFICIENT ||
            c.languageProficiency === LANGUAGE_PROFICIENCIES_KEYS.FLUENT)
      ) ?? [];

    if (matchedLanguages.length > 0 && matchedLanguages[0]?.name) {
      return {
        name: `checklist-item-language`,
        icon: MatchChecklistIcons.Matched,
        children: `${
          matchedLanguages.some(
            ml => ml.languageProficiency === LANGUAGE_PROFICIENCIES_KEYS.FLUENT
          )
            ? 'Fluent'
            : 'Business Proficient'
        } in ${matchedLanguages[0].name}`,
      };
    }

    return undefined;
  },

  getAvailabilityChecklistItem(
    weeklyAvailability: Candidate['weeklyAvailability'],
    billingUnitsPerWeek: AccountIntake['billingUnitsPerWeek']
  ): MatchChecklistItemType | undefined {
    if (!billingUnitsPerWeek) {
      return undefined;
    }

    const isMatched =
      weeklyAvailability && weeklyAvailability >= billingUnitsPerWeek;

    return {
      name: 'checklist-item-availability',
      icon: isMatched
        ? MatchChecklistIcons.Matched
        : MatchChecklistIcons.Warning,
      children: isMatched ? 'Available now' : 'Not currently available',
    };
  },

  getFocusAreaChecklistItem(
    candidateExperiences: Experience[],
    focusArea: AccountIntake['primaryFocusArea'],
    taxonomy: Taxonomy
  ): MatchChecklistItemType | undefined {
    if (candidateExperiences.length === 0 || !focusArea) {
      return undefined;
    }
    const focusAreaLabel = taxonomy.focusAreas[focusArea]?.fullName;

    if (!focusAreaLabel) {
      return undefined;
    }

    const matchingExperiences = candidateExperiences.filter(experience =>
      experience.experienceTaxonomy.some(
        expTaxonomyItem => expTaxonomyItem.focusArea === focusArea
      )
    );

    if (matchingExperiences.length > 0) {
      const numNonDsSuggested = matchingExperiences.filter(exp =>
        exp.experienceTaxonomy.some(
          expTaxonomyItem =>
            !expTaxonomyItem.dsSuggestion &&
            expTaxonomyItem.focusArea === focusArea
        )
      ).length;

      const isAIChecklistItem = numNonDsSuggested === 0;

      const experienceCount = isAIChecklistItem
        ? matchingExperiences.length
        : numNonDsSuggested;

      const experienceText =
        experienceCount === 1
          ? `${focusAreaLabel} experience in 1 engagement`
          : `${focusAreaLabel} experience in ${experienceCount} engagements`;

      return isAIChecklistItem
        ? {
            name: `checklist-item-ai-focus-area-${focusArea}`,
            icon: MatchChecklistIcons.AI,
            children: experienceText,
          }
        : {
            name: `checklist-item-focus-area-${focusArea}`,
            icon: MatchChecklistIcons.Matched,
            children: experienceText,
          };
    }

    return undefined;
  },

  getLocationChecklistItem(
    desiredState: string,
    worksite: PositionWorksitesType,
    candidateState: Candidate['addressState']
  ): MatchChecklistItemType | undefined {
    if (worksite !== Onsite && worksite !== OnsiteRemote) {
      return undefined;
    }

    const response = {
      name: 'checklist-item-location',
      children: candidateState
        ? `Based in ${candidateState}`
        : 'Unknown address state',
    } as MatchChecklistItemType;

    const validStates = getValidStates(desiredState.toLowerCase());

    response.icon = validStates.includes(candidateState?.toLowerCase())
      ? MatchChecklistIcons.Matched
      : MatchChecklistIcons.Warning;

    return response;
  },

  getLegalTechSkillsChecklistItems(
    desiredLegalTechSkills: AccountIntake['legalTechSkills'],
    candidateLegalTechSkills: CandidateLegalTechSkill[],
    taxonomy: Taxonomy
  ): MatchChecklistItemType[] {
    if (
      !desiredLegalTechSkills?.length ||
      candidateLegalTechSkills.length === 0
    ) {
      return [];
    }

    const candidateSkills = new Set(
      candidateLegalTechSkills.flatMap(skill => skill.legalTechSkill)
    );

    return desiredLegalTechSkills
      .filter(desiredSkill => candidateSkills.has(desiredSkill))
      .sort()
      .map(skill => ({
        name: `checklist-item-legal-tech-skill-${skill}`,
        icon: MatchChecklistIcons.Matched,
        children: taxonomy.legalTechSkills[skill]?.displayName || skill,
      }));
  },

  getGeneralSkillsChecklistItems(
    desiredGeneralSkills: AccountIntake['generalSkills'],
    candidateExperiences: Experience[],
    taxonomy: Taxonomy
  ): MatchChecklistItemType[] {
    if (!desiredGeneralSkills?.length || candidateExperiences.length === 0) {
      return [];
    }

    const candidateGeneralSkills = new Set(
      candidateExperiences
        .filter(exp => exp.generalSkills && exp.generalSkills.length > 0)
        .flatMap(exp => exp.generalSkills)
    );

    if (candidateGeneralSkills.size === 0) {
      return [];
    }

    return desiredGeneralSkills
      .filter(desiredSkill => candidateGeneralSkills.has(desiredSkill))
      .sort()
      .map(skill => ({
        name: `checklist-item-general-skill-${skill}`,
        icon: MatchChecklistIcons.Matched,
        children: taxonomy.generalSkills[skill]?.displayName || skill,
      }));
  },

  getLegalSkillsChecklistItem(
    candidateExperiences: Experience[],
    desiredLegalSkill: NonNullable<AccountIntake['legalSkills']>[number],
    taxonomy: Taxonomy
  ): MatchChecklistItemType | undefined {
    if (candidateExperiences.length === 0 || !desiredLegalSkill) {
      return undefined;
    }

    const legalSkillLabel = taxonomy.legalSkills[desiredLegalSkill]?.fullName;

    if (!legalSkillLabel) {
      return undefined;
    }

    const matchingExperiences = candidateExperiences.filter(experience =>
      experience.experienceTaxonomy.some(expTaxonomyItem =>
        expTaxonomyItem.legalSkills?.includes(desiredLegalSkill)
      )
    );

    if (matchingExperiences.length > 0) {
      const hasNonDsSuggested = matchingExperiences.some(exp =>
        exp.experienceTaxonomy.some(
          expTaxonomyItem =>
            !expTaxonomyItem.dsSuggestion &&
            expTaxonomyItem.legalSkills?.includes(desiredLegalSkill)
        )
      );

      const isAIChecklistItem = !hasNonDsSuggested;

      return isAIChecklistItem
        ? {
            name: `checklist-item-ai-legal-skill-${desiredLegalSkill}`,
            icon: MatchChecklistIcons.AI,
            children: legalSkillLabel,
          }
        : {
            name: `checklist-item-legal-skill-${desiredLegalSkill}`,
            icon: MatchChecklistIcons.Matched,
            children: legalSkillLabel,
          };
    }

    return undefined;
  },
};
